import { DashboardCustomizeOutlined, DeleteOutline, ForwardToInbox, GroupOutlined, ContentCopyOutlined } from '@mui/icons-material';
import moment from 'moment';
import React from 'react';
import Constants from 'relevant-shared/reportData/constants';
import { UserAvatar } from '../Avatar';
import { Link } from '../Link/Link';
import { Tooltip } from '../Tooltip';
import { useCopyToDashboard } from '../ViewEditReport/hooks/useCopyToDashboard';
import useCopyReport from '../ViewEditReport/hooks/useCopyReport';
import useDeleteReport from '../ViewEditReport/hooks/useDeleteReport';
import { usePinReport } from '../ViewEditReport/hooks/usePinReport';
import { PinButton } from '../ViewEditReport/PinButton';
import { getReportTypeIcon } from './utils';
import { MoreMenu, MenuItem } from '../MoreMenu/MoreMenu';
import { DateBadge } from './DateBadge';

type ReportCardProps = {
	skeleton?: boolean;
	type?: 'programmatic' | 'audience' | 'hb' | 'summary_hb' | 'future' | 'log';
	title?: string;
	createdAt?: string;
	author?: {
		id: string;
		fullname: string;
		initials: string;
		picture: string;
		isDeleted?: boolean;
	};
	isPublic?: boolean;
	image?: string;
	link?: string;
	report?: {
		id: string;
		pinnedByUsers: string[];
		settings: any;
		subscriptions: any[];
		userId: string;
		isPublic: boolean;
	};
	subscriptions?: any[];
}

export function ReportCard({
	skeleton = false,
	title,
	createdAt,
	author,
	isPublic,
	type,
	image,
	link = '#',
	report,
	subscriptions = []
}: ReportCardProps) {
	const { pinned, setPinned } = usePinReport({ report });
	const { copyToDashboard } = useCopyToDashboard({ report });
	const deleteReport = useDeleteReport({ reportId: report?.id, report });
	const openCopyDialog = useCopyReport({ report, type });

	return (
		<div className="flex flex-col h-[325px] bg-white rounded-lg border border-borders-primary hover:border-borders-primary_hover hover:shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)] transition-shadow duration-50">
			{/* Content */}
			<Link unstyled to={link} className="flex flex-col gap-6 items-center p-6 pb-4 cursor-pointer group">
				<div className="flex flex-col w-full gap-2">
					<h3 className="text-text-primary text-md font-medium w-full truncate">
						{skeleton ? (
							<span className="animate-pulse bg-grey-100 rounded h-6 w-3/4 block">
								{/* Skeleton placeholder */}
							</span>
						) : title}
					</h3>
					<DateBadge report={report} skeleton={skeleton} />
				</div>

				{/* Chart/Graph Area */}
				<div className="w-full h-[100px] max-h-[100px]">
					{skeleton ? (
						<div className="w-full h-full animate-pulse bg-grey-100 rounded" />
					) : (
						<img src={image} className="w-full h-full object-contain" />
					)}
				</div>

				<div className="flex flex-row justify-between items-center w-full text-sm text-text-secondary h-[24px]">
					<Tooltip title={!skeleton && createdAt && moment(createdAt).format('LLL')}>
						{skeleton ? (
							<span className="animate-pulse bg-grey-100 rounded h-full w-32 block" />
						) : (
							<span>{createdAt && `Created ${moment(createdAt).fromNow()}`}</span>
						)}
					</Tooltip>
					<div className="flex flex-row items-center gap-6">
						{(skeleton || author) && (
							<Tooltip title={`${author?.fullname || ''}${author?.isDeleted ? ' (deactivated)' : ''}`}>
								{skeleton ? (
									<div className="animate-pulse bg-grey-100 rounded-full size-5" />
								) : (
									<UserAvatar
										userId={author.id}
										fallbackUser={author}
										className="min-w-5! min-h-5! m-[2px]"
									/>
								)}
							</Tooltip>
						)}
						{(skeleton || isPublic) && (
							<Tooltip title="Public">
								{skeleton ? (
									<div className="animate-pulse bg-grey-100 rounded size-5" />
								) : (
									<GroupOutlined className="text-fg-tertiary size-[20px]!" />
								)}
							</Tooltip>
						)}
						{(skeleton || subscriptions.length > 0) && (
							<Tooltip title={`${subscriptions.length} subscription${subscriptions.length > 1 ? 's' : ''}`}>
								{skeleton ? (
									<div className="animate-pulse bg-grey-100 rounded size-5" />
								) : (
									<div className="relative">
										<ForwardToInbox className="text-fg-tertiary size-[20px]!" />
									</div>
								)}
							</Tooltip>
						)}
					</div>
				</div>
			</Link>

			{/* Footer */}
			<div className="flex items-center justify-between text-sm text-grey-650 border-t border-borders-primary px-6 py-3 gap-2 min-w-0 overflow-hidden">
				<div className="flex gap-[10px] items-center shrink min-w-0 h-[30px]">
					{skeleton ? (
						<>
							<div className="animate-pulse bg-grey-100 rounded size-5" />
							<span className="animate-pulse bg-grey-100 rounded h-4 w-24 block" />
						</>
					) : (
						<>
							<img src={getReportTypeIcon(type)} alt={Constants[type].DESCRIPTION} className="size-5" />
							<span className="text-sm text-text-secondary font-semibold leading-5 truncate">{Constants[type].DESCRIPTION}</span>
						</>
					)}
				</div>
				{!skeleton && (
					<div className="flex items-center gap-2 shrink-0 -mr-1">
						<PinButton size="s" variant="favourite" active={pinned} onToggle={setPinned} />
						<MoreMenu size="s">
							<MenuItem icon={<ContentCopyOutlined />} onClick={openCopyDialog} disabled={!report?.settings}>Make a copy</MenuItem>
							<MenuItem icon={<DashboardCustomizeOutlined />} onClick={copyToDashboard}>Copy to Dashboard</MenuItem>
							<MenuItem icon={<DeleteOutline />} onClick={deleteReport} className="hover:text-fg-highlight">Delete</MenuItem>
						</MoreMenu>
					</div>
				)}
			</div>
		</div>
	);
}
